import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  useRef,
  useState,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { MediaOwner, SupplierDetails, VendorType } from "models/supplier"
import {
  PatchSupplierPayload,
  fetchSupplier,
  patchSupplier,
} from "api/suppliers"
import { queryCache, useMutation, useQuery } from "react-query"

//
import Autocomplete from "@material-ui/lab/Autocomplete"
import { Country } from "models/common"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import FormControl from "@material-ui/core/FormControl"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import FormLabel from "@material-ui/core/FormLabel"
import { Get } from "utils/Get"
import { Loading } from "components/Loading"
import Radio from "@material-ui/core/Radio"
import RadioGroup from "@material-ui/core/RadioGroup"
import TextField from "@material-ui/core/TextField"
import { TextInput } from "components/inputs/TextInput"
import _set from "lodash/fp/set"
import { css } from "@emotion/css"
import { fetchCompanies } from "api/companies"
import { fetchCountriesByRegion } from "api/geography"
import get from "lodash/get"
import set from "lodash/set"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router"
import { useSendFeedBack } from "components/FeedbackSnackbar"

//

const emptyPayload: Partial<SupplierDetails> = {}

const vendorTypeOptions = [
  { value: "PUBLIC", label: "vendor.fields.vendorType.PUBLIC" },
  { value: "INTERCOMPANY", label: "vendor.fields.vendorType.INTERCOMPANY" },
]
const mediaOwnerOptions = [
  { value: "MEDIA_OWNER", label: "vendor.fields.mediaOwner.MEDIA_OWNER" },
  {
    value: "CONNECTED_VENDOR",
    label: "vendor.fields.mediaOwner.CONNECTED_VENDOR",
  },
]

export default function Profile() {
  const { id } = useParams<{ id: string }>()

  const {
    data: vendor,
    isLoading,
    updatedAt,
    /*
      This is the same data as in ./index
      _but_ on patch success
      we invalidate only the ./index data with query key "market-vendor-details"
      because _reasons_
    */
  } = useQuery(["market-vendor-details-profile", id], fetchSupplier, {
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    )
  }

  return <ProfileForm vendor={vendor!} updatedAt={updatedAt} />
}

type CompanyOption = { name: string; id: string }

function ProfileForm(props: { vendor: SupplierDetails; updatedAt: number }) {
  const { vendor, updatedAt } = props
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const sendFeedback = useSendFeedBack()
  const { hasAuthority } = useAuthenticated()

  const [patch, patchResult] = useMutation<
    SupplierDetails,
    PatchSupplierPayload,
    ApiError
  >(patchSupplier, {
    onError: (e: ApiError, v) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      const fieldName = Object.keys(v.data)[0]
      queryCache.invalidateQueries("market-vendor-details-profile")
      sendFeedback(t(errorMap[fieldName]) || t(error?.message || ""), "error")
    },
    onSuccess: () => {
      queryCache.invalidateQueries("market-vendor-details")
    },
  })

  const { getTextInputProps, getFieldProps } = useFieldManager<SupplierDetails>(
    {
      initialValues: vendor!,
      updatedAt,
      errors: patchResult.error?.errorsMap,
      commit: (name, value) => {
        const data = set(emptyPayload, name, value)
        return patch({ id, data })
      },
    },
  )

  const countryField = getFieldProps("address.countryCode", "")
  const vendorTypeField = getFieldProps("vendorType", "PUBLIC")
  const mediaOwnerField = getFieldProps("mediaOwner", "MEDIA_OWNER")
  const companyIdField = getFieldProps("companyId", "")
  const iCannotChangeVendorType = (type: string) =>
    type === "INTERCOMPANY" && vendor?.mediaOwner === "MEDIA_OWNER"

  return (
    <div className={styles.main}>
      <section className={styles.section}>
        <h2>{t("vendors.profile.section.details")}</h2>
        <div className="fields">
          <div className={styles.fieldRow}>
            <TextInput
              name="localName"
              label={t("vendors.fields.localName")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["localName"] ?? ""}
            />
            <TextInput
              name="code"
              label={t("vendors.fields.code")}
              margin="normal"
              size="medium"
              className={styles.fieldWidthShorter}
              disabled
              value={vendor?.["code"] ?? ""}
            />
            <TextInput
              name="groupName"
              label={t("vendors.fields.groupName")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["groupName"] ?? ""}
            />
            <TextInput
              name="rutNumber"
              label={t("vendors.fields.rutNumber")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["rutNumber"] ?? ""}
            />
          </div>
          <div className={styles.fieldRow}>
            <TextInput
              name="marketName"
              label={t("vendors.fields.marketName")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["marketName"] ?? ""}
            />
            <TextInput
              name="subRegionName"
              label={t("vendors.fields.subRegionName")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["subRegionName"] ?? ""}
            />
            <TextInput
              name="regionName"
              label={t("vendors.fields.regionName")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled
              value={vendor?.["regionName"] ?? ""}
            />
          </div>

          <FormControl component="fieldset" className={styles.radioControl}>
            <FormLabel className={styles.radioControlLabel}>
              {t("vendors.fields.vendorType")}
            </FormLabel>
            <RadioGroup
              className={styles.radioGroup}
              aria-label="vendor type"
              name="vendorType"
              value={vendorTypeField.value}
              onChange={(_, value) => {
                const data: Partial<SupplierDetails> = {
                  vendorType: value as VendorType,
                }
                vendorTypeField.setFieldValue(value as VendorType)
                value === "PUBLIC" && patch({ id, data })
              }}
            >
              {vendorTypeOptions.map((vt) => (
                <FormControlLabel
                  key={vt.value}
                  value={vt.value}
                  label={t(vt.label)}
                  control={<Radio color="primary" />}
                  disabled={
                    !hasAuthority(["AUTH_EDIT_SUPPLIER"]) ||
                    iCannotChangeVendorType(vt.value)
                  }
                />
              ))}
            </RadioGroup>
            {vendorTypeField.value === "INTERCOMPANY" ? (
              <Companies
                label={t("vendors.fields.company")}
                name="companyId"
                companyId={companyIdField.value ?? ""}
                value={companyIdField.value!}
                onValueChange={(v) => {
                  const companyId = !v.length ? null : v
                  const vendorType: VendorType = !v.length
                    ? "PUBLIC"
                    : "INTERCOMPANY"
                  companyIdField.setFieldValue(v)

                  const data: Partial<SupplierDetails> = { vendorType }
                  !!companyId && (data.companyId = companyId)

                  patch({ id, data }).then(() => {
                    vendorTypeField.setFieldValue(vendorType)
                  })
                }}
                className={styles.fieldWidth}
              />
            ) : null}
          </FormControl>
          <FormControl component="fieldset" className={styles.radioControl}>
            <FormLabel className={styles.radioControlLabel}>
              {t("vendors.fields.mediaOwner")}
            </FormLabel>
            <RadioGroup
              className={styles.radioGroup}
              aria-label="media owner"
              name="mediaOwner"
              value={mediaOwnerField.value}
              onChange={(_, value) => {
                const data: Partial<SupplierDetails> = {
                  mediaOwner: value as MediaOwner,
                }
                mediaOwnerField.setFieldValue(value as MediaOwner)
                patch({ id, data })
              }}
            >
              {mediaOwnerOptions.map((x) => (
                <FormControlLabel
                  key={x.value}
                  value={x.value}
                  label={t(x.label)}
                  control={<Radio color="primary" />}
                  disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </div>
      </section>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("supplier-groups.profile.addresses")}
        </span>
        <div className={styles.addresses}>
          {t("supplier-groups.profile.business-address")}
        </div>
        <div className="fields">
          <TextInput
            label={t("vendors.fields.address.address1")}
            margin="normal"
            size="medium"
            className={styles.fieldWidthWider}
            {...getTextInputProps("address.address1", "")}
            disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
          />
          <TextInput
            label={t("vendors.fields.address.address2")}
            margin="normal"
            size="medium"
            className={styles.fieldWidthWider}
            {...getTextInputProps("address.address2", "")}
            disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
          />
          <div className={styles.fieldRow}>
            <Countries
              label={t("vendors.fields.address.countryCode")}
              name="address.countryCode"
              regionCode={vendor?.["regionCode"] ?? ""}
              value={countryField.value!}
              onValueChange={(v) => countryField.setFieldValue(v)}
              onBlur={countryField.commitFieldValue}
              className={styles.fieldWidth}
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
            />
            <TextInput
              label={t("vendors.fields.address.state")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              {...getTextInputProps("address.state", "")}
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
            />
          </div>
          <div className={styles.fieldRow}>
            <TextInput
              label={t("vendors.fields.address.city")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              {...getTextInputProps("address.city", "")}
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
            />
            <TextInput
              label={t("vendors.fields.address.zipCode")}
              margin="normal"
              size="medium"
              className={styles.zipCode}
              {...getTextInputProps("address.zipCode", "")}
              disabled={!hasAuthority(["AUTH_EDIT_SUPPLIER"])}
            />
          </div>
        </div>
      </section>
    </div>
  )
}

const styles = {
  main: css`
    margin-top: var(--spacing-xs);
  `,
  loadingContainer: css`
    display: grid;
    place-items: center;
    min-height: 400px;
    width: 100%;
  `,
  section: css`
    display: flex;
    flex-direction: column;
    padding-bottom: var(--spacing-l);
    padding-top: var(--spacing-xl);

    > h2,
    .title {
      font-size: 0.875rem;
      font-weight: bold;
      color: var(--black, #000);
      margin-bottom: calc(var(--spacing-xl, 24px) - 16px);
    }

    .fields {
      display: flex;
      flex-direction: column;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    text-transform: capitalize;
  `,
  addresses: css`
    display: flex;
    flex-wrap: wrap;
    margin-top: var(--spacing-xl);
    margin-bottom: var(--spacing-xs);
    font-size: 14px;
    font-weight: 300;
    color: var(--black);
  `,
  fieldRow: css`
    display: flex;
    gap: var(--spacing-m, 16px);
  `,
  fieldWidth: css`
    width: 233px;
  `,
  zipCode: css`
    width: 158px;
  `,
  fieldWidthShorter: css`
    width: 160px;
  `,
  fieldWidthWider: css`
    width: 485px;
  `,
  radioControl: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xl, 24px);
  `,
  radioControlLabel: css`
    color: var(--black) !important;
    font-size: 13px;
    font-weight: 300;
  `,
  radioGroup: css`
    display: flex;
    flex-direction: row;
  `,
}

type UseFieldManagerProps<Values extends object> = {
  initialValues: Values
  updatedAt: number
  commit?: <Path extends string>(
    name: Path,
    value: Get<Values, Path>,
  ) => Promise<unknown>
  errors?: ErrorsMap
}
function useFieldManager<Values extends object>(
  props: UseFieldManagerProps<Values>,
) {
  const { updatedAt, initialValues, commit, errors } = props
  const lastValues = useRef<Values>(initialValues)
  const lastUpdatedAt = useRef<number>(updatedAt)
  const [values, setValues] = useState<Values>(() => initialValues)

  const [errorMap, setErrorMap] = useState<ErrorsMap | undefined>(
    () => undefined,
  )
  const lastErrors = useRef(errors)

  if (updatedAt !== lastUpdatedAt.current) {
    lastValues.current = initialValues
    lastUpdatedAt.current = updatedAt
    setValues(() => initialValues)
  }

  if (errors !== lastErrors.current) {
    lastErrors.current = errors
    setErrorMap(errors)
  }

  function setValue<Path extends string, Value extends Get<Values, Path>>(
    name: Path,
    value: Value,
  ): void {
    setValues(_set(name, value))
  }

  function getFieldProps<Path extends string, Value extends Get<Values, Path>>(
    name: Path,
    defaultValue: Value,
  ) {
    const value = tget(values, name, defaultValue)
    const errorMessage = errorMap?.[name]

    const setFieldValue = (newValue: Value): void => {
      setValues(_set(name, newValue))
      setErrorMap((s) => {
        const _s = { ...s }
        delete _s[name]
        return _s
      })
    }

    const commitFieldValue = (): void => {
      if (value !== tget(lastValues.current, name)) {
        if (commit) {
          commit(name, value).then(() => {
            set(lastValues.current, name, value)
          })
        } else {
          set<Values>(lastValues.current, name, value)
        }
      }
    }

    return {
      value,
      setFieldValue,
      commitFieldValue,
      errorMessage,
    }
  }

  function getTextInputProps<
    Path extends string,
    Value extends Get<Values, Path>,
  >(name: Path, defaultValue: Value) {
    const { value, setFieldValue, commitFieldValue, errorMessage } =
      getFieldProps(name, defaultValue)
    const onChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
      const newValue = evt.target.value as Value
      setFieldValue(newValue)
    }

    const onBlur: FocusEventHandler<HTMLInputElement> = (_) => {
      commitFieldValue()
    }

    return {
      value,
      onChange,
      onBlur,
      name,
      errorMessage,
    }
  }

  return {
    getTextInputProps,
    getFieldProps,
    setValue,
  }
}

function tget<
  BaseType extends Record<string | number, any>,
  Path extends string,
>(
  o: BaseType,
  path: Path,
  defaultValue?: Get<BaseType, Path>,
): Get<BaseType, Path> {
  return get(o, path, defaultValue) as Get<BaseType, Path>
}

type CountriesProps = {
  regionCode?: string
  name: string
  label: string
  value: string
  onValueChange: (value: string, name?: string) => void
  onBlur: () => void
  className?: string
  disabled?: boolean
}

export function Countries(props: CountriesProps) {
  const {
    regionCode = "",
    name,
    label,
    value,
    onValueChange,
    onBlur,
    className,
    disabled,
  } = props

  const { data: countries } = useQuery(
    ["countries-by-region", regionCode],
    fetchCountriesByRegion,
    {
      enabled: !!regionCode,
    },
  )

  const options = countries?.data ?? []
  const option = options?.find((opt) => opt.code === value)

  const handleChange = (_: ChangeEvent<{}>, value: Country | null) => {
    onValueChange(value?.code ?? "", name)
  }

  return (
    <Autocomplete<Country>
      getOptionLabel={(opt) => opt.name}
      value={option || null}
      onChange={handleChange}
      onBlur={onBlur}
      options={options || []}
      openOnFocus={false}
      filterSelectedOptions
      getOptionSelected={(opt) => opt?.code === value}
      popupIcon={<DropdownIcon className="icon" />}
      className={className}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          name={name}
          label={label}
          variant="outlined"
          margin="normal"
          size="medium"
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
    />
  )
}

type CompaniesProps = {
  companyId?: string
  name: string
  label: string
  value: string
  onValueChange: (value: string, name?: string) => void
  onBlur?: () => void
  className?: string
  disabled?: boolean
}

export function Companies(props: CompaniesProps) {
  const { name, label, value, onValueChange, onBlur, className, disabled } =
    props

  const { data: companies } = useQuery(["companies"], fetchCompanies)

  const options = companies?.data ?? []
  const option = options?.find((opt) => opt.id === value)

  const handleChange = (_: ChangeEvent<{}>, value: CompanyOption | null) => {
    onValueChange(value?.id ?? "", name)
  }

  return (
    <Autocomplete<CompanyOption>
      getOptionLabel={(opt) => opt.name}
      value={option || null}
      onChange={handleChange}
      onBlur={onBlur}
      options={options || []}
      openOnFocus={false}
      filterSelectedOptions
      getOptionSelected={(opt) => opt?.id === value}
      popupIcon={<DropdownIcon className="icon" />}
      className={className}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          name={name}
          label={label}
          variant="outlined"
          margin="normal"
          size="medium"
          InputProps={{
            ...params.InputProps,
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "no",
          }}
        />
      )}
    />
  )
}
